import {ClaimManagerFrontend} from '../../environments/application-configurations';
import {environment} from '../../environments/environment';
import {Condominium} from '../entity/condominium';
import {Injectable} from '@angular/core';
import {AuthService} from '../../authentication/auth-service';


@Injectable({
  providedIn: 'root'
})
export class CondominiumListService {
  private readonly localStorageKey: string = `${environment.claimManagerFrontend.baseUrl}:${this.constructor.name}`
  private readonly frontendConfig: ClaimManagerFrontend = environment.claimManagerFrontend
  private condominiumList: Condominium[] = [];
  private readonly condominiumCache: Map<string, Condominium> = new Map<string, Condominium>();

  constructor(private readonly authService: AuthService) {
    this.clearCache();
  }

  async getCondominiumList(): Promise<Condominium[]> {
    if (this.authService.userHasRole('insurance_company')) {
      this.clearCache();
      return this.getCondominiumListFor(
        '_data',
        this.authService.getCurrentUser()?.profile?.['custom:insurance_company_id'] as string
      ).then(l => {
        this.clearCache();
        return l;
      });
    } else {
      return this.getCondominiumListFor(
        this.authService.getCurrentUser()?.profile?.['custom:adjuster_company_id'] as string,
        this.authService.getCurrentUser()?.profile?.['custom:insurance_company_id'] as string
      )
    }
  }

  async getCondominiumListFor(adjusterCompanyId: string | undefined | null, insuranceCompanyId: string = "_insurance_"): Promise<Condominium[]> {
    if (adjusterCompanyId == null || adjusterCompanyId.length < 1) {
      console.warn('No adjuster company id provided');
      return Promise.resolve([]);
    }

    if (this.condominiumList.length > 0) {
      return Promise.resolve(this.condominiumList);
    } else {
      this.condominiumList = await this.getFromCacheOrFetchFromServer(adjusterCompanyId, insuranceCompanyId);
      this.condominiumCache.clear();
      this.condominiumList?.forEach(condominium => {
        this.condominiumCache.set(condominium.condominium, condominium)
      })
    }
    return Promise.resolve(this.condominiumList);
  }

  public clearCache(): void {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(environment.claimManagerFrontend.baseUrl)) {
        localStorage.removeItem(key);
      }
    }
    this.condominiumList = []
    this.condominiumCache.clear();
  }

  public getCondominiumByName(condoName: string): Condominium | undefined {
    return this.condominiumCache.get(condoName);
  }

  private getFromCacheOrFetchFromServer(adjusterCompanyId: string, insuranceCompanyId: string = "_insurance_"): Promise<Condominium[]> {
    const condominiumListString = localStorage.getItem(this.getKey(adjusterCompanyId, insuranceCompanyId));
    if (condominiumListString && condominiumListString.length > 2) {
      try {
        return Promise.resolve(JSON.parse(condominiumListString) as Condominium[]);
      } catch (e) {
        console.error('Error parsing condominium list from cache:', e);
      }
    }

    return this.fetchFromServerPutInCache(adjusterCompanyId, insuranceCompanyId);
  }

  private async fetchFromServerPutInCache(adjusterCompanyId: string, insuranceCompanyId: string = "_insurance_"): Promise<Condominium[]> {
    try {
      const path = this.frontendConfig.condominiumListPath
        .replace("{insuranceCompanyId}", insuranceCompanyId.trim())
        .replace("{adjusterCompanyId}", adjusterCompanyId.trim())
        .toLowerCase();
      let response = await fetch(`${this.frontendConfig.baseUrl}${path}`);
      let condoList: Condominium[] = [];
      if (!response.ok) {
        console.error('Error fetching condominium list from server:', response.statusText, response.text());
      } else {
        condoList = (await response.json()) as Condominium[];
        const condoTable: Record<string, Condominium> = {};
        condoList?.forEach(condo => {
          condoTable[condo.condominium] = condo;
        })
        condoList = Object.values(condoTable).sort((a, b) => a.condominium.localeCompare(b.condominium));
      }
      // localStorage.setItem(this.getKey(adjusterCompanyId, insuranceCompanyId), JSON.stringify(condoList));
      return condoList;
    } catch (e) {
      console.warn('Error fetching condominium list from server:', e);
      return [];
    }
  }

  private getKey(adjusterCompanyId: string, insuranceCompanyId: string) {
    return `${this.localStorageKey}:${adjusterCompanyId}:${insuranceCompanyId}`.toLowerCase();
  }
}
